import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';

const EMPTY_FORM = {
    plan_name: '',
    lender: '',
    amount: '',
    initial_interest_rate: '',
    term_in_years: '',
    origination_date: '',
    first_payment_date: '',
    n_payments_per_year: 26,
    rate_type: 'Fixed',
    amortizing: true,
    debt_type: '401k_loan',
    total_closing_costs: 0,
};

const FourOhOneKLoanList = () => {
    const [loans, setLoans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [form, setForm] = useState(EMPTY_FORM);
    const [submitting, setSubmitting] = useState(false);
    const [formError, setFormError] = useState('');

    const fetchLoans = () => {
        setLoading(true);
        axios.get('/api/balance_sheet/financing/401k_loans/')
            .then(response => {
                setLoans(response.data);
                setLoading(false);
            })
            .catch(error => {
                console.error('Error fetching 401k loan data:', error);
                setLoading(false);
            });
    };

    useEffect(() => {
        fetchLoans();
    }, []);

    const handleRemove = async (id) => {
        if (!window.confirm('Are you sure you want to remove this loan?')) return;
        try {
            await axios.delete(`/api/balance_sheet/financing/401k_loans/${id}/`);
            setLoans((prev) => prev.filter(loan => loan.id !== id));
        } catch (error) {
            console.error('Error removing loan:', error);
            alert('There was an error removing the loan.');
        }
    };

    const handleFormChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setFormError('');
        setSubmitting(true);
        try {
            // Resolve lender name → ID
            const lendersRes = await axios.get('/api/balance_sheet/financing/lender-choices/');
            const lenders = lendersRes.data || [];
            let lenderId = lenders.find(([, name]) => name === form.lender)?.[0];
            if (!lenderId) {
                const lenderRes = await axios.post('/api/balance_sheet/financing/lender-choices/', { name: form.lender });
                lenderId = lenderRes.data.id;
            }
            await axios.post('/api/balance_sheet/financing/401k_loans/', {
                ...form,
                lender: lenderId,
                amount: parseFloat(form.amount),
                initial_interest_rate: parseFloat(form.initial_interest_rate),
                term_in_years: parseFloat(form.term_in_years),
                n_payments_per_year: parseInt(form.n_payments_per_year, 10),
            });
            setShowModal(false);
            setForm(EMPTY_FORM);
            fetchLoans();
        } catch (error) {
            console.error('Error creating 401k loan:', error);
            const detail = error.response?.data
                ? JSON.stringify(error.response.data)
                : 'Failed to create loan.';
            setFormError(detail);
        } finally {
            setSubmitting(false);
        }
    };

    const formatCurrency = (amount) => `$${Math.round(amount).toLocaleString()}`;

    return (
        <div style={{ marginLeft: '40px' }}>
            <div style={{ margin: '20px' }}>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <h1 style={{ marginRight: '20px' }}>401k Loan List</h1>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <button
                            className="btn btn-primary"
                            onClick={() => { setShowModal(true); setFormError(''); }}
                        >
                            + New 401k Loan
                        </button>
                        <Link to="/balance_sheet/financing/">All Debt</Link>
                    </div>
                </div>
            </div>

            {loading ? (
                <div style={{ margin: '20px' }}>Loading 401k loans...</div>
            ) : (
                <table className="loan-table">
                    <thead>
                        <tr>
                            <th>Loan ID</th>
                            <th>Plan Name</th>
                            <th>Lender</th>
                            <th>Loan Amount</th>
                            <th>Interest Rate</th>
                            <th>Term (Years)</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loans.length > 0 ? (
                            loans.map(loan => (
                                <tr key={loan.id}>
                                    <td>
                                        <Link to={`/balance_sheet/financing/401k_loans/${loan.id}`}>{loan.id}</Link>
                                    </td>
                                    <td>{loan.plan_name}</td>
                                    <td>{loan.lender}</td>
                                    <td>{formatCurrency(loan.amount)}</td>
                                    <td>{loan.initial_interest_rate}%</td>
                                    <td>{loan.term_in_years}</td>
                                    <td>
                                        <button type="button" className="btn btn-danger" onClick={() => handleRemove(loan.id)}>Remove</button>
                                    </td>
                                </tr>
                            ))
                        ) : (
                            <tr>
                                <td colSpan="7" style={{ textAlign: 'center' }}>No 401k loans found.</td>
                            </tr>
                        )}
                    </tbody>
                </table>
            )}

            {/* Create New 401k Loan Modal */}
            {showModal && (
                <div style={styles.overlay}>
                    <div style={styles.modal}>
                        <div style={styles.modalHeader}>
                            <h3 style={{ margin: 0 }}>New 401k Loan</h3>
                            <button
                                style={styles.closeBtn}
                                onClick={() => setShowModal(false)}
                            >✕</button>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div style={styles.grid}>
                                <div style={styles.field}>
                                    <label style={styles.label}>Plan Name</label>
                                    <input
                                        style={styles.input}
                                        name="plan_name"
                                        value={form.plan_name}
                                        onChange={handleFormChange}
                                        placeholder="e.g. Fidelity 401k"
                                    />
                                </div>
                                <div style={styles.field}>
                                    <label style={styles.label}>Lender / Administrator</label>
                                    <input
                                        style={styles.input}
                                        name="lender"
                                        value={form.lender}
                                        onChange={handleFormChange}
                                        placeholder="e.g. Fidelity"
                                        required
                                    />
                                </div>
                                <div style={styles.field}>
                                    <label style={styles.label}>Loan Amount ($)</label>
                                    <input
                                        style={styles.input}
                                        type="number"
                                        name="amount"
                                        value={form.amount}
                                        onChange={handleFormChange}
                                        placeholder="e.g. 10000"
                                        required
                                    />
                                </div>
                                <div style={styles.field}>
                                    <label style={styles.label}>Annual Interest Rate (%)</label>
                                    <input
                                        style={styles.input}
                                        type="number"
                                        step="0.01"
                                        name="initial_interest_rate"
                                        value={form.initial_interest_rate}
                                        onChange={handleFormChange}
                                        placeholder="e.g. 5.00"
                                        required
                                    />
                                </div>
                                <div style={styles.field}>
                                    <label style={styles.label}>Term (Years)</label>
                                    <input
                                        style={styles.input}
                                        type="number"
                                        step="0.01"
                                        name="term_in_years"
                                        value={form.term_in_years}
                                        onChange={handleFormChange}
                                        placeholder="e.g. 5"
                                        required
                                    />
                                </div>
                                <div style={styles.field}>
                                    <label style={styles.label}>Payments Per Year</label>
                                    <input
                                        style={styles.input}
                                        type="number"
                                        name="n_payments_per_year"
                                        value={form.n_payments_per_year}
                                        onChange={handleFormChange}
                                        placeholder="26 (bi-weekly)"
                                    />
                                </div>
                                <div style={styles.field}>
                                    <label style={styles.label}>Origination Date</label>
                                    <input
                                        style={styles.input}
                                        type="date"
                                        name="origination_date"
                                        value={form.origination_date}
                                        onChange={handleFormChange}
                                        required
                                    />
                                </div>
                                <div style={styles.field}>
                                    <label style={styles.label}>First Payment Date</label>
                                    <input
                                        style={styles.input}
                                        type="date"
                                        name="first_payment_date"
                                        value={form.first_payment_date}
                                        onChange={handleFormChange}
                                        required
                                    />
                                </div>
                            </div>
                            {formError && (
                                <div style={{ color: '#f87171', marginTop: '10px', fontSize: '13px' }}>
                                    {formError}
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px', marginTop: '20px' }}>
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => setShowModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    type="submit"
                                    className="btn btn-primary"
                                    disabled={submitting}
                                >
                                    {submitting ? 'Creating...' : 'Create Loan'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <style jsx>{`
                .loan-table {
                    margin: 20px;
                    width: 90%;
                    table-layout: auto;
                    border-collapse: collapse;
                }
                .loan-table th, .loan-table td {
                    border: 1px solid #374151;
                    padding: 10px;
                    text-align: left;
                }
                .loan-table th {
                    background-color: #1f2937;
                    color: #e5e7eb;
                }
                .loan-table td {
                    color: #e5e7eb;
                }
            `}</style>
        </div>
    );
};

const styles = {
    overlay: {
        position: 'fixed',
        top: 0, left: 0, right: 0, bottom: 0,
        backgroundColor: 'rgba(0,0,0,0.6)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
    },
    modal: {
        backgroundColor: '#1f2937',
        borderRadius: '8px',
        padding: '24px',
        width: '600px',
        maxWidth: '95vw',
        maxHeight: '90vh',
        overflowY: 'auto',
        color: '#e5e7eb',
    },
    modalHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
    },
    closeBtn: {
        background: 'none',
        border: 'none',
        color: '#e5e7eb',
        fontSize: '18px',
        cursor: 'pointer',
    },
    grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        gap: '16px',
    },
    field: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    label: {
        fontSize: '13px',
        color: '#9ca3af',
    },
    input: {
        backgroundColor: '#374151',
        border: '1px solid #4b5563',
        borderRadius: '4px',
        padding: '8px',
        color: '#e5e7eb',
        fontSize: '14px',
    },
};

export default FourOhOneKLoanList;
